import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import CTable from "../../../components/Table/table";
import { getWaEventsRequest } from "../../../modules/admin-modules/reports/Actions";

const formatDateTimeParam = (value) => {
  if (!value) return "";
  return value.includes("T") ? value.replace("T", " ") : value;
};

const formatMessage = (message) => {
  if (message == null || message === "") return "";
  if (typeof message === "string") return message;
  try {
    return JSON.stringify(message, null, 2);
  } catch {
    return String(message);
  }
};

const ExpandableText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const value = formatMessage(text);

  if (!value) {
    return <Text>-</Text>;
  }

  const isLong = value.length > 80 || value.includes("\n");

  return (
    <Box maxW="320px">
      <Text
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        noOfLines={expanded || !isLong ? undefined : 2}
      >
        {value}
      </Text>
      {isLong && (
        <Button
          variant="link"
          size="sm"
          color="main_1"
          mt={1}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? "See less" : "See more"}
        </Button>
      )}
    </Box>
  );
};

const WaFailedEvents = () => {
  const dispatch = useDispatch();
  const waEvents = useSelector((state) => state.reports.waEvents);
  const isLoading = useSelector((state) => state.reports.isLoading);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [size, setPerPage] = useState(15);

  const handleFetch = () => {
    if (!from || !to) {
      toast.error("Please select both from and to date/time");
      return;
    }
    setPageNumber(0);
    dispatch(
      getWaEventsRequest({
        from: formatDateTimeParam(from),
        to: formatDateTimeParam(to),
      })
    );
  };

  const Data = useMemo(
    () =>
      (Array.isArray(waEvents) ? waEvents : []).map((event, index) => ({
        ...event,
        index: `#${index + 1}`,
        created_at: event?.created_at
          ? new Date(event.created_at).toLocaleString()
          : "-",
        message_display: <ExpandableText text={event?.message} />,
      })),
    [waEvents]
  );

  const columns = [
    { Header: "#", accessor: "index" },
    { Header: "Message ID", accessor: "message_id" },
    { Header: "Message", accessor: "message_display" },
    { Header: "Recipient Phone", accessor: "recipient_phone" },
    { Header: "WhatsApp Account", accessor: "whatsapp_account" },
    { Header: "Error Message", accessor: "error_message" },
    { Header: "Error Details", accessor: "error_details" },
    { Header: "Created At", accessor: "created_at" },
  ];

  const exportToExcel = () => {
    if (!Data.length) {
      toast.error("No events to export");
      return;
    }

    const sheetData = Data.map((event) => ({
      "#": event.index,
      "Message ID": event.message_id || "",
      Message: formatMessage(event.message),
      "Recipient Phone": event.recipient_phone || "",
      "WhatsApp Account": event.whatsapp_account || "",
      "Error Message": event.error_message || "",
      "Error Details": event.error_details || "",
      "Created At": event.created_at || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    worksheet["!cols"] = [
      { width: 8 },
      { width: 40 },
      { width: 50 },
      { width: 20 },
      { width: 28 },
      { width: 28 },
      { width: 40 },
      { width: 22 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "WA Failed Events");
    const fileName = `wa-failed-events-${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="10000000"
          flexWrap="wrap"
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            WA Failed Events
          </Text>
          <Flex alignItems="center" gap={3} flexWrap="wrap">
            <Button
              bg="main_1"
              color="white"
              onClick={exportToExcel}
              isDisabled={!Data.length}
            >
              Export to Excel
            </Button>
            <LogoutMenu />
          </Flex>
        </Flex>

        <Box mb={6} p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="16px" fontWeight="500" mb={4}>
            Filter by date range
          </Text>
          <Flex gap={4} alignItems="end" flexWrap="wrap">
            <FormControl maxW="280px">
              <FormLabel>From</FormLabel>
              <Input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </FormControl>
            <FormControl maxW="280px">
              <FormLabel>To</FormLabel>
              <Input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </FormControl>
            <Button
              bg="main_1"
              color="white"
              onClick={handleFetch}
              isLoading={isLoading}
              isDisabled={!from || !to}
            >
              Fetch
            </Button>
          </Flex>
        </Box>

        <CTable
          selectedData={() => {}}
          Columns={columns}
          Data={Data}
          path={"openModel"}
          Actions
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar={true}
          noFilter={true}
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={() => {}}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar={true}
          isLoading={isLoading}
          perPage={size}
          totalPage={1}
        />
      </div>
    </Layout>
  );
};

export default WaFailedEvents;
